import { apiFetch } from '../../common/api/apiFetchClient';

export interface ActiveSurveySummary {
  createdDate: string | null;
  modifiedDate: string | null;
  createdBy: string | null;
  modifiedBy: string | null;
  surveyId: number;
  name: string | null;
  description: string | null;
  startDate: string | null;
  active: boolean;
  endDate: string | null;
}

export interface SurveyEntrySummary {
  entryId: string;
  userId: string;
  createdAt: string;
  expiresAt: string;
  activeSurvey: ActiveSurveySummary;
  selectedCustomer: {
    customerNumber: string;
    employerGroupName: string;
    employerGroupEIN: string | null;
    policyNumber: string;
    policyList: string;
    fundingType: string;
    surveyStatus: string;
    surveyYear: string;
    surveyId: number | null;
  };
}

export interface RuntimeSurveyBranchRule {
  branchingId: number;
  questionOptionId: number;
  jumpToQuestionId: number;
  active: boolean;
}

export interface RuntimeSurveyQuestionOption {
  questionOptionId: number;
  optionTitle: string;
  orderId: number;
  active: boolean;
}

export interface RuntimeSurveyQuestion {
  questionId: number;
  questionTitle: string;
  questionTypeId: number;
  questionType?: string | null;
  required: boolean;
  active: boolean;
  orderId: number | null;
  surveyQuestionDbCode: string | null;
  inputFormat: string | null;
  minLength: number | null;
  maxLength: number | null;
  errorMessage: string | null;
  helpText: string | null;
  helpLink: string | null;
  helpLinkLabel: string | null;
  options: RuntimeSurveyQuestionOption[];
  branchRules: RuntimeSurveyBranchRule[];
}

export interface SurveyResponseDetailOptionSelection {
  rdOptionsId: string | null;
  responseDetailsId: string | null;
  questionOptionId: number;
}

export interface SurveyResponseDetail {
  responseDetailsId: string | null;
  surveyResponseId: string | null;
  questionId: number;
  response: string | null;
  surveyResponseDetailsOptionsDTOList: SurveyResponseDetailOptionSelection[];
}

export interface SurveyResponseRecord {
  surveyResponseId: string | null;
  surveyId: number | null;
  userName: string | null;
  userId: number | null;
  status: string | null;
  customerNumber: string | null;
  fundingType: string | null;
  reportingYear: number | null;
  attested: number | null;
  startDate: string | null;
  completedDate: string | null;
  surveyResponseDetailsDTOList: SurveyResponseDetail[];
}

interface SurveyEntryLookupResponse {
  entry: SurveyEntrySummary;
}

interface RuntimeSurveyQuestionsResponse {
  questions: RuntimeSurveyQuestion[];
}

export async function getSurveyEntry(args: {
  entryId: string;
}): Promise<SurveyEntrySummary> {
  const body = await apiFetch<SurveyEntryLookupResponse>(
    `/api/rfi/survey/entry/${encodeURIComponent(args.entryId)}`,
  );
  return body.entry;
}

export async function getSurveyQuestionsBySurveyId(args: {
  surveyId: number;
}): Promise<RuntimeSurveyQuestion[]> {
  const body = await apiFetch<RuntimeSurveyQuestionsResponse>(
    `/api/rfi/survey/${encodeURIComponent(String(args.surveyId))}/questions`,
  );
  return body.questions;
}

export async function getSurveyResponseByCustomerIdentity(args: {
  customerNumber: string;
  fundingType: string;
  reportingYear: number;
  surveyId: number;
}): Promise<SurveyResponseRecord | null> {
  const query = new URLSearchParams({
    customerNumber: args.customerNumber,
    fundingType: args.fundingType,
    reportingYear: String(args.reportingYear),
    surveyId: String(args.surveyId),
  });

  const url = `/api/rfi/survey/response?${query.toString()}`;
  return apiFetch<SurveyResponseRecord | null>(url, {
    method: 'GET',
  });
}

export async function saveSurveyResponse(args: {
  payload: SurveyResponseRecord;
}): Promise<SurveyResponseRecord> {
  return apiFetch<SurveyResponseRecord>(
    '/api/rfi/survey/response/save',
    {
      method: 'POST',
      body: JSON.stringify(args.payload),
    },
  );
}
